import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReferralsManager from './ReferralsManager';
import FormPreview from './FormPreview';
import { FORM_TEMPLATES, buildTemplateFields } from './formTemplates';
import { formPublicUrl } from './publicFormUrl';
import { defaultSuccessSteps } from './publicFormUi';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL });
API.interceptors.request.use(config => {
  const token = localStorage.getItem('cms_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: 'Aa' },
  { value: 'textarea', label: 'Textarea', icon: '¶' },
  { value: 'email', label: 'Email', icon: '@' },
  { value: 'number', label: 'Number', icon: '##' },
  { value: 'date', label: 'Date', icon: '▦' },
  { value: 'checkbox', label: 'Checkbox', icon: '☑' },
  { value: 'radio', label: 'Radio', icon: '◉' },
  { value: 'select', label: 'Select', icon: '▼' },
  { value: 'image', label: 'Image upload', icon: '▣' },
  { value: 'file', label: 'File upload', icon: '📎' },
];

const uid = () => Math.random().toString(36).slice(2, 8);
const slugify = s => {
  if (!s || typeof s !== 'string') return '';
  return s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

export default function FormBuilder({ form, onUpdate, userRole, project, forms = [] }) {
  const [formData, setFormData] = useState(form);
  const [showFieldAdd, setShowFieldAdd] = useState(false);
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [expandedFieldId, setExpandedFieldId] = useState(null);
  const [autoSaved, setAutoSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loopsLists, setLoopsLists] = useState([]);
  const [loopsError, setLoopsError] = useState('');
  const [loopsTeam, setLoopsTeam] = useState('');
  const [loopsMissing, setLoopsMissing] = useState([]);
  const [loopsAssigned, setLoopsAssigned] = useState([]);
  const [loopsTransactionals, setLoopsTransactionals] = useState([]);
  const [testValues, setTestValues] = useState({});
  const [testResult, setTestResult] = useState('');
  const [testing, setTesting] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [ogUploading, setOgUploading] = useState(false);
  const [builderTab, setBuilderTab] = useState('settings'); // settings | preview
  const autoSaveTimer = useRef(null);
  const skipAutosave = useRef(true);
  const saveChain = useRef(Promise.resolve());
  const latestFormData = useRef(form);

  useEffect(() => {
    skipAutosave.current = true;
    setFormData({
      ...form,
      showLoadingScreen: form?.showLoadingScreen === true,
      formsReferralsSection: form?.formsReferralsSection === true,
      showReferral: form?.showReferral !== false,
      requireApproval: form?.requireApproval === true,
      showConfirmCopy: form?.showConfirmCopy !== false,
      showShareLink: form?.showShareLink !== false,
      submitLabel: form?.submitLabel || '',
      receivedTitle: form?.receivedTitle || '',
      thankYouMessage: form?.thankYouMessage || '',
      notifyEmail: form?.notifyEmail || '',
      loopsListId: form?.loopsListId || '',
      loopsTransactionalId: form?.loopsTransactionalId || '',
      loopsNotifyTransactionalId: form?.loopsNotifyTransactionalId || '',
      loopsApproveTransactionalId: form?.loopsApproveTransactionalId || '',
      loopsDenyTransactionalId: form?.loopsDenyTransactionalId || '',
      slug: form?.slug || '',
      buttonColor: form?.buttonColor || '#7C3AED',
      successSteps: Array.isArray(form?.successSteps) && form.successSteps.length
        ? form.successSteps
        : defaultSuccessSteps(form)
    });
    setTestValues({});
    setTestResult('');
  }, [form?._id]);

  useEffect(() => {
    latestFormData.current = formData;
  }, [formData]);

  const persistForm = async (data) => {
    setSaving(true);
    try {
      await onUpdate(data);
      setSaveError('');
      setAutoSaved(true);
    } catch (err) {
      setSaveError(err?.response?.data?.error || err.message || 'Save failed');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const queuePersist = () => {
    saveChain.current = saveChain.current
      .catch(() => {})
      .then(() => persistForm(latestFormData.current));
    return saveChain.current;
  };

  useEffect(() => {
    if (skipAutosave.current) {
      skipAutosave.current = false;
      return;
    }
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      queuePersist();
    }, 800);

    return () => clearTimeout(autoSaveTimer.current);
  }, [formData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get('/api/forms/meta/loops-lists');
        if (!cancelled) {
          setLoopsLists(res.data?.data || []);
          setLoopsTeam(res.data?.meta?.teamName || '');
          setLoopsMissing(res.data?.meta?.missing || []);
          setLoopsAssigned(res.data?.meta?.assigned || []);
          setLoopsTransactionals(res.data?.meta?.transactionals || []);
          setLoopsError(res.data?.warning || '');
        }
      } catch (e) {
        if (!cancelled) {
          setLoopsLists([]);
          setLoopsTeam('');
          setLoopsMissing([]);
          setLoopsAssigned([]);
          setLoopsTransactionals([]);
          setLoopsError(e.response?.data?.error || 'Could not load Loops lists');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAddField = () => {
    if (!newFieldLabel.trim()) return;
    const newField = {
      id: uid(),
      label: newFieldLabel.trim(),
      name: newFieldLabel.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
      type: newFieldType,
      required: false,
      placeholder: '',
      options: ['checkbox', 'radio', 'select'].includes(newFieldType) ? ['Option 1', 'Option 2'] : []
    };
    const updated = {
      ...formData,
      fields: [...(formData.fields || []), newField]
    };
    setFormData(updated);
    setExpandedFieldId(newField.id);
    setNewFieldLabel('');
    setNewFieldType('text');
    setShowFieldAdd(false);
  };

  const handleRemoveField = (fieldId) => {
    const updated = {
      ...formData,
      fields: (formData.fields || []).filter(f => f.id !== fieldId)
    };
    setFormData(updated);
  };

  const handleMoveField = (fieldId, direction) => {
    const fields = [...(formData.fields || [])];
    const index = fields.findIndex(f => f.id === fieldId);
    if (index < 0) return;
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= fields.length) return;
    const [moved] = fields.splice(index, 1);
    fields.splice(target, 0, moved);
    setFormData({ ...formData, fields });
  };

  const handleUpdateField = (fieldId, changes) => {
    const updated = {
      ...formData,
      fields: (formData.fields || []).map(f => f.id === fieldId ? { ...f, ...changes } : f)
    };
    setFormData(updated);
  };

  const handleUpdateFormMeta = (key, value) => {
    const updated = { ...formData, [key]: value };
    setFormData(updated);
  };

  const handleTestSubmit = async () => {
    if (!formData?._id) return;
    setTesting(true);
    setTestResult('');
    try {
      const data = {};
      for (const field of formData.fields || []) {
        if (testValues[field.name] != null && testValues[field.name] !== '') {
          data[field.name] = testValues[field.name];
        }
      }
      const res = await API.post(`/api/forms/${formData._id}/submit`, {
        data,
        referralCode: data.referral_code || data.ref || ''
      });
      const loops = res.data?.loops;
      const email = res.data?.email;
      const notify = res.data?.notify;
      const loopsMsg = !loops
        ? ''
        : loops.ok
          ? ' · Loops ✓'
          : loops.reason === 'pending_approval'
            ? ' · Loops waits for approve'
            : ` · Loops failed (${loops.reason || 'unknown'})`;
      const emailMsg = !email
        ? ''
        : email.ok
          ? ' · confirm email ✓'
          : ` · confirm email skipped (${email.reason || 'no template'})`;
      const notifyMsg = !notify
        ? ''
        : notify.ok
          ? ' · team notify ✓'
          : ` · team notify skipped (${notify.reason || 'no inbox'})`;
      setTestResult(
        `Submitted ✓ ${res.data?.entryId || 'ok'}` +
        (res.data?.referralCode ? ` · referral ${res.data.referralCode} counted` : ' · no referral counted') +
        loopsMsg +
        emailMsg +
        notifyMsg
      );
    } catch (e) {
      setTestResult(e.response?.data?.error || e.message);
    }
    setTesting(false);
  };

  const isViewOnly = userRole === 'viewer';
  const assignedList = loopsAssigned.find(item => item.slug === formData.slug);

  useEffect(() => {
    if (isViewOnly || !assignedList?.listId) return;
    if (formData.loopsListId === assignedList.listId) return;
    handleUpdateFormMeta('loopsListId', assignedList.listId);
  }, [assignedList?.listId, formData.slug]);
  const publicUrl = formPublicUrl(formData.slug || slugify(formData.title));

  const copyText = async (text, kind = 'url') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedUrl(kind);
      setTimeout(() => setCopiedUrl(false), 1600);
    } catch {
      window.prompt('Copy:', text);
    }
  };

  const handleSaveNow = async () => {
    if (isViewOnly) return;
    clearTimeout(autoSaveTimer.current);
    try {
      await queuePersist();
    } catch (err) {
      // saveError already set
    }
  };

  const applySiHerDefiFields = () => {
    if (isViewOnly) return;
    if (!window.confirm('This will ERASE the current fields and replace them with the Si Her DeFi template. This is not Save. Continue?')) return;
    const fields = buildTemplateFields('si-her-defi');
    const template = FORM_TEMPLATES['si-her-defi'];
    setFormData(d => ({
      ...d,
      title: template.title || d.title,
      description: template.description || d.description,
      buttonColor: template.buttonColor || d.buttonColor || '#6B46FE',
      showLoadingScreen: false,
      formsReferralsSection: true,
      fields
    }));
  };

  const moveToFormsReferrals = () => {
    if (isViewOnly) return;
    setFormData(d => ({ ...d, formsReferralsSection: true }));
  };

  const saveLabel = saveError
    ? 'Save failed'
    : saving
      ? 'Saving…'
      : autoSaved
        ? 'Saved'
        : 'Save';

  return (
    <div style={{ maxWidth: 960, padding: '20px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className={builderTab === 'settings' ? 'btn-solid' : 'btn-ghost'} style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setBuilderTab('settings')}>Settings & fields</button>
        <button className={builderTab === 'preview' ? 'btn-solid' : 'btn-ghost'} style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setBuilderTab('preview')}>UX preview</button>
        {!isViewOnly && !formData.formsReferralsSection && (
          <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 12px' }} onClick={moveToFormsReferrals}>
            Move to Forms + Referrals
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {saveError ? (
            <span style={{ fontSize: 12, color: '#b91c1c' }}>Save failed: {saveError}</span>
          ) : saving ? (
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Saving…</span>
          ) : autoSaved ? (
            <span style={{ fontSize: 12, color: 'var(--accent)' }}>All changes saved</span>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Autosave on</span>
          )}
          {!isViewOnly && (
            <button
              className="btn-solid"
              style={{ fontSize: 12, padding: '6px 14px' }}
              onClick={handleSaveNow}
              disabled={saving}
            >
              {saveLabel}
            </button>
          )}
        </div>
      </div>

      {builderTab === 'preview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Form URL (automatic)</div>
              <div style={{ fontSize: 13, wordBreak: 'break-all' }}>{publicUrl || 'Set a slug to generate URL'}</div>
            </div>
            <button
              type="button"
              className="btn-solid"
              style={{ fontSize: 12, padding: '6px 10px' }}
              disabled={!publicUrl}
              onClick={() => copyText(publicUrl, 'public')}
            >
              {copiedUrl === 'public' ? 'Copied' : 'Copy form URL'}
            </button>
          </div>
          <FormPreview form={formData} />
        </div>
      )}

      {builderTab === 'settings' && (
      <>
      {/* Form Metadata */}
      <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Form Settings</h3>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Title</label>
          <input
            className="minput"
            value={formData.title}
            onChange={e => handleUpdateFormMeta('title', e.target.value)}
            disabled={isViewOnly}
            placeholder="Form title"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Slug</label>
          <input
            className="minput"
            value={formData.slug || slugify(formData.title)}
            onChange={e => handleUpdateFormMeta('slug', e.target.value)}
            disabled={isViewOnly}
            placeholder="form-slug"
          />
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 4 }}>
            Becomes the public path: <code>{publicUrl || 'https://si3.space/your-slug'}</code>
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Description</label>
          <textarea
            className="minput"
            rows={2}
            value={formData.description}
            onChange={e => handleUpdateFormMeta('description', e.target.value)}
            disabled={isViewOnly}
            placeholder="Form description"
          />
        </div>

        <div style={{ marginBottom: 16, padding: 14, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)' }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>Form URL (automatic)</label>
          <div style={{
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            fontSize: 13,
            wordBreak: 'break-all'
          }}>
            {publicUrl || 'https://si3.space/{slug}'}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button"
              className="btn-solid"
              style={{ fontSize: 12, padding: '6px 10px' }}
              disabled={!publicUrl}
              onClick={() => copyText(publicUrl, 'public')}
            >
              {copiedUrl === 'public' ? 'Copied form URL' : 'Copy form URL'}
            </button>
            {publicUrl && (
              <a className="btn-ghost" href={publicUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, padding: '6px 10px', textDecoration: 'none' }}>
                Open form
              </a>
            )}
          </div>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 8 }}>
            This is the live form link. Existing buttons that still point at the old path will open this popup — you do not need to recode every CTA. Referral links append <code>?ref=CODE</code> automatically.
          </small>
        </div>

        <div style={{ marginBottom: 16, padding: 14, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)' }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13 }}>Brand button color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <input
              type="color"
              value={/^#([0-9A-Fa-f]{6})$/.test(formData.buttonColor || '') ? formData.buttonColor : '#7C3AED'}
              onChange={e => handleUpdateFormMeta('buttonColor', e.target.value)}
              disabled={isViewOnly}
              style={{ width: 44, height: 36, padding: 0, border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', cursor: isViewOnly ? 'default' : 'pointer' }}
            />
            <input
              className="minput"
              style={{ width: 120, fontFamily: 'var(--mono)', fontSize: 12 }}
              value={formData.buttonColor || '#7C3AED'}
              onChange={e => handleUpdateFormMeta('buttonColor', e.target.value)}
              disabled={isViewOnly}
              placeholder="#7C3AED"
            />
            {['#7C3AED', '#DB2777', '#0F766E', '#1D4ED8', '#111111'].map(color => (
              <button
                key={color}
                type="button"
                title={color}
                disabled={isViewOnly}
                onClick={() => handleUpdateFormMeta('buttonColor', color)}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  border: (formData.buttonColor || '#7C3AED') === color ? '2px solid #111' : '1px solid var(--border)',
                  background: color,
                  cursor: isViewOnly ? 'default' : 'pointer',
                  padding: 0
                }}
              />
            ))}
          </div>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 8 }}>
            Each cloned form can have its own submit-button color. Clone this form, then change this color.
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Submit button text</label>
          <input
            className="minput"
            value={formData.submitLabel || ''}
            onChange={e => handleUpdateFormMeta('submitLabel', e.target.value)}
            disabled={isViewOnly}
            placeholder="Submit Info"
          />
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 4 }}>
            Leave blank for the default. Speakers default to Submit Info.
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>After-submit heading</label>
          <input
            className="minput"
            value={formData.receivedTitle || ''}
            onChange={e => handleUpdateFormMeta('receivedTitle', e.target.value)}
            disabled={isViewOnly}
            placeholder="SUBMISSION RECEIVED"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>After-submit line</label>
          <input
            className="minput"
            value={formData.thankYouMessage || ''}
            onChange={e => handleUpdateFormMeta('thankYouMessage', e.target.value)}
            disabled={isViewOnly}
            placeholder="your submission is in."
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isViewOnly ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.showConfirmCopy !== false}
              onChange={e => handleUpdateFormMeta('showConfirmCopy', e.target.checked)}
              disabled={isViewOnly}
            />
            <span style={{ fontSize: 13 }}>Show “we sent a confirmation email” on the thank-you screen</span>
          </label>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isViewOnly ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.showShareLink !== false}
              onChange={e => handleUpdateFormMeta('showShareLink', e.target.checked)}
              disabled={isViewOnly}
            />
            <span style={{ fontSize: 13 }}>Show referral/share link on the thank-you screen</span>
          </label>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isViewOnly ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.showLoadingScreen === true}
              onChange={e => handleUpdateFormMeta('showLoadingScreen', e.target.checked)}
              disabled={isViewOnly}
            />
            <span style={{ fontSize: 13 }}>Show loading screen when form opens</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 4 }}>
            Off by default — public form UIs should not flash a loading splash.
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isViewOnly ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.showReferral !== false}
              onChange={e => handleUpdateFormMeta('showReferral', e.target.checked)}
              disabled={isViewOnly}
            />
            <span style={{ fontSize: 13 }}>Show referral code on this form</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 4 }}>
            Turn this off for forms that should not collect a referrer (like speakers).
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isViewOnly ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.requireApproval === true}
              onChange={e => handleUpdateFormMeta('requireApproval', e.target.checked)}
              disabled={isViewOnly}
            />
            <span style={{ fontSize: 13 }}>Approve in CMS before sending to Loops</span>
          </label>
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 4 }}>
            Applicant still saves to CRM as Applied. Approve or deny them from the Applied list.
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Loops List</label>
          {loopsLists.length > 0 ? (
            <select
              className="minput"
              value={formData.loopsListId || ''}
              onChange={e => handleUpdateFormMeta('loopsListId', e.target.value)}
              disabled={isViewOnly}
            >
              <option value="">— Not connected —</option>
              {loopsLists.map(list => (
                <option key={list.id} value={list.id}>
                  {list.name}{assignedList?.listId === list.id ? ' (this form)' : ''}
                </option>
              ))}
            </select>
          ) : (
            <div style={{
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              fontSize: 13
            }}>
              {formData.loopsListId
                ? `Connected list ID: ${formData.loopsListId}`
                : 'Not connected yet'}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <small style={{ color: 'var(--muted)', alignSelf: 'center' }}>
              {assignedList
                ? `This form uses “${assignedList.listName}”`
                : (formData.loopsListId
                  ? `Connected list ID: ${formData.loopsListId}`
                  : (loopsError || 'Select the matching Loops list for this form'))}
            </small>
          </div>
          {loopsTeam && (
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: 6 }}>
              Loops workspace: {loopsTeam}
            </small>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Loops confirmation email</label>
          {loopsTransactionals.length > 0 ? (
            <select
              className="minput"
              value={formData.loopsTransactionalId || ''}
              onChange={e => handleUpdateFormMeta('loopsTransactionalId', e.target.value)}
              disabled={isViewOnly}
            >
              <option value="">— No confirmation email —</option>
              {loopsTransactionals.map(item => (
                <option key={item.id || item.transactionalId} value={item.id || item.transactionalId}>
                  {item.name || item.title || item.id || item.transactionalId}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="minput"
              value={formData.loopsTransactionalId || ''}
              onChange={e => handleUpdateFormMeta('loopsTransactionalId', e.target.value)}
              disabled={isViewOnly}
              placeholder="Paste Loops transactional ID"
            />
          )}
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 6 }}>
            Sends the Loops transactional email on submit. Create that email in Loops first, then paste its ID here. Leave empty for speakers (no confirm email).
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Notify team email</label>
          <input
            className="minput"
            value={formData.notifyEmail || ''}
            onChange={e => handleUpdateFormMeta('notifyEmail', e.target.value)}
            disabled={isViewOnly}
            placeholder="dao@si3.space"
          />
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 4 }}>
            When someone submits, this inbox gets a Loops notify event (and a transactional email if you paste an ID below).
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Staff notify transactional ID</label>
          <input
            className="minput"
            value={formData.loopsNotifyTransactionalId || ''}
            onChange={e => handleUpdateFormMeta('loopsNotifyTransactionalId', e.target.value)}
            disabled={isViewOnly}
            placeholder="Optional Loops transactional ID"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Approve email transactional ID</label>
          <input
            className="minput"
            value={formData.loopsApproveTransactionalId || ''}
            onChange={e => handleUpdateFormMeta('loopsApproveTransactionalId', e.target.value)}
            disabled={isViewOnly}
            placeholder="Si Her DAO / DeFi approve email ID"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Deny email transactional ID</label>
          <input
            className="minput"
            value={formData.loopsDenyTransactionalId || ''}
            onChange={e => handleUpdateFormMeta('loopsDenyTransactionalId', e.target.value)}
            disabled={isViewOnly}
            placeholder="Si Her DAO / DeFi deny email ID"
          />
        </div>

        <div style={{ marginBottom: 16, padding: 14, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)' }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>After-submit steps</label>
          <small style={{ color: 'var(--muted)', display: 'block', marginBottom: 10 }}>
            These 3 steps show on the “received” screen. Each form can have its own copy.
          </small>
          {(formData.successSteps?.length ? formData.successSteps : defaultSuccessSteps(formData)).slice(0, 3).map((step, index) => (
            <div key={index} style={{ display: 'grid', gridTemplateColumns: '52px 1fr', gap: 8, marginBottom: 10, alignItems: 'start' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', paddingTop: 10 }}>0{index + 1}</div>
              <div>
                <input
                  className="minput"
                  value={step.title || ''}
                  disabled={isViewOnly}
                  placeholder="Step title"
                  onChange={e => {
                    const next = [...(formData.successSteps?.length ? formData.successSteps : defaultSuccessSteps(formData))];
                    next[index] = { ...next[index], title: e.target.value };
                    handleUpdateFormMeta('successSteps', next);
                  }}
                />
                <input
                  className="minput"
                  style={{ marginTop: 6 }}
                  value={step.detail || ''}
                  disabled={isViewOnly}
                  placeholder="Step detail"
                  onChange={e => {
                    const next = [...(formData.successSteps?.length ? formData.successSteps : defaultSuccessSteps(formData))];
                    next[index] = { ...next[index], detail: e.target.value };
                    handleUpdateFormMeta('successSteps', next);
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Form Image</label>
          {formData.image && (
            <div style={{ position: 'relative', width: 150, marginBottom: 8 }}>
              <img
                src={formData.image}
                alt="form"
                style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 6 }}
                onError={e => e.target.style.display = 'none'}
              />
              {!isViewOnly && (
                <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 8px', marginTop: 6, width: '100%' }} onClick={() => handleUpdateFormMeta('image', '')}>
                  Remove
                </button>
              )}
            </div>
          )}
          {!isViewOnly && (
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
              onClick={() => document.getElementById('form-img')?.click()}>
              <span style={{fontSize:12,color:'var(--muted2)'}}>⬆ {formData.image ? 'Replace' : 'Upload'} image</span>
              <input id="form-img" type="file" accept="image/*" style={{display:'none'}}
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append('image', file);
                  try {
                    const res = await fetch(process.env.REACT_APP_API_URL + '/api/upload', {method:'POST', body:fd});
                    const data = await res.json();
                    if (data.url) handleUpdateFormMeta('image', data.url);
                  } catch(err) { alert('Upload failed'); }
                }} />
            </div>
          )}
          <small style={{ color: 'var(--muted)', display: 'block', marginTop: 6 }}>
            Decorative only — not used as a loading screen.
          </small>
        </div>

        <div style={{ marginBottom: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: 13 }}>Meta Tags (SEO)</h4>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12 }}>Meta Title</label>
            <input
              className="minput"
              value={formData.meta?.title || ''}
              onChange={e => handleUpdateFormMeta('meta', { ...formData.meta, title: e.target.value })}
              disabled={isViewOnly}
              placeholder="Meta title for SEO"
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12 }}>Meta Description</label>
            <textarea
              className="minput"
              rows={2}
              value={formData.meta?.description || ''}
              onChange={e => handleUpdateFormMeta('meta', { ...formData.meta, description: e.target.value })}
              disabled={isViewOnly}
              placeholder="Meta description for SEO"
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12 }}>Open Graph Image (link preview)</label>
            {formData.meta?.image && (
              <div style={{ position: 'relative', width: 240, marginBottom: 8 }}>
                <img
                  src={formData.meta.image}
                  alt="open graph preview"
                  style={{ width: '100%', aspectRatio: '1200 / 630', objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)' }}
                  onError={e => e.target.style.display = 'none'}
                />
                {!isViewOnly && (
                  <button
                    className="btn-ghost"
                    style={{ fontSize: 11, padding: '4px 8px', marginTop: 6, width: '100%' }}
                    onClick={() => handleUpdateFormMeta('meta', { ...formData.meta, image: '' })}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
            {!isViewOnly && (
              <>
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor: ogUploading ? 'default' : 'pointer'}}
                  onClick={() => { if (!ogUploading) document.getElementById('form-og-img')?.click(); }}>
                  <span style={{fontSize:12,color:'var(--muted2)'}}>
                    {ogUploading ? 'Uploading…' : `⬆ ${formData.meta?.image ? 'Replace' : 'Upload'} share image`}
                  </span>
                  <input id="form-og-img" type="file" accept="image/*" style={{display:'none'}}
                    onChange={async e => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      const fd = new FormData();
                      fd.append('image', file);
                      setOgUploading(true);
                      try {
                        const res = await fetch(process.env.REACT_APP_API_URL + '/api/upload', {method:'POST', body:fd});
                        const data = await res.json();
                        if (data.url) handleUpdateFormMeta('meta', { ...latestFormData.current.meta, image: data.url });
                        else alert('Upload failed');
                      } catch(err) { alert('Upload failed'); }
                      finally { setOgUploading(false); }
                    }} />
                </div>
                <input
                  className="minput"
                  style={{ marginTop: 8 }}
                  value={formData.meta?.image || ''}
                  onChange={e => handleUpdateFormMeta('meta', { ...formData.meta, image: e.target.value })}
                  placeholder="…or paste an image URL"
                />
              </>
            )}
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: 6 }}>
              Shown when this form&apos;s link is shared on X, LinkedIn, Telegram, Slack, etc. Use 1200×630px.
              Leave empty to fall back to the Form Image, then the SI&lt;3&gt; default.
            </small>
          </div>
        </div>
      </div>

      {/* Form Fields */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Form Fields</h3>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Use ↑ ↓ to reorder</span>
        </div>

        {(!formData.fields || formData.fields.length === 0) ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--muted)', background: 'var(--bg)', borderRadius: 6 }}>
            No fields added yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {formData.fields.map((field, index) => (
              <FieldEditor
                key={field.id}
                field={field}
                index={index}
                total={formData.fields.length}
                expanded={expandedFieldId === field.id}
                onToggleExpand={() => setExpandedFieldId(expandedFieldId === field.id ? null : field.id)}
                onUpdate={(changes) => handleUpdateField(field.id, changes)}
                onRemove={() => handleRemoveField(field.id)}
                onMoveUp={() => handleMoveField(field.id, 'up')}
                onMoveDown={() => handleMoveField(field.id, 'down')}
                disabled={isViewOnly}
              />
            ))}
          </div>
        )}

        {!isViewOnly && (
          <div style={{ marginTop: 20 }}>
            {!showFieldAdd ? (
              <button className="btn-solid" onClick={() => setShowFieldAdd(true)}>
                + Add Field
              </button>
            ) : (
              <div style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)' }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Field Type</label>
                  <select
                    value={newFieldType}
                    onChange={e => setNewFieldType(e.target.value)}
                    className="minput"
                  >
                    {FIELD_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13 }}>Field Label</label>
                  <input
                    type="text"
                    value={newFieldLabel}
                    onChange={e => setNewFieldLabel(e.target.value)}
                    className="minput"
                    placeholder="e.g. Email Address, Full Name"
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-solid" onClick={handleAddField} style={{ fontSize: 12, padding: '8px 16px' }}>Add Field</button>
                  <button className="btn-ghost" onClick={() => setShowFieldAdd(false)} style={{ fontSize: 12, padding: '8px 16px' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {!isViewOnly && (
        <div style={{ padding: 14, border: '1px dashed var(--border)', borderRadius: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Danger zone</div>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--muted)' }}>
            This is not Save. It wipes the current questions and loads the Si Her DeFi template.
          </p>
          <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 12px', color: '#b91c1c' }} onClick={applySiHerDefiFields}>
            Reset fields to template
          </button>
        </div>
      )}

      {/* Live test submit — helps verify referral metrics + Loops */}
      {!isViewOnly && formData?._id && (
        <div style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: 15 }}>Test submit</h3>
          <p style={{ margin: '0 0 12px 0', fontSize: 12, color: 'var(--muted)' }}>
            Submit through the real API to verify referral tracking and Loops. Use an active referral code like <code>KARATEST</code>.
          </p>
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {(formData.fields || []).map(field => (
              <div key={field.id}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>{field.label}{field.required ? ' *' : ''}</label>
                {field.type === 'textarea' ? (
                  <textarea
                    className="minput"
                    rows={2}
                    value={testValues[field.name] || ''}
                    onChange={e => setTestValues(v => ({ ...v, [field.name]: e.target.value }))}
                    placeholder={field.placeholder || ''}
                  />
                ) : field.type === 'select' || field.type === 'radio' ? (
                  <select
                    className="minput"
                    value={testValues[field.name] || ''}
                    onChange={e => setTestValues(v => ({ ...v, [field.name]: e.target.value }))}
                  >
                    <option value="">Select…</option>
                    {(field.options || []).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="minput"
                    type={field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                    value={testValues[field.name] || ''}
                    onChange={e => setTestValues(v => ({ ...v, [field.name]: e.target.value }))}
                    placeholder={field.placeholder || ''}
                  />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="btn-solid" onClick={handleTestSubmit} disabled={testing}>
              {testing ? 'Submitting…' : 'Submit test'}
            </button>
            {testResult && <span style={{ fontSize: 12, color: 'var(--muted)' }}>{testResult}</span>}
          </div>
        </div>
      )}

      {/* Referral codes directly under this form */}
      {project && formData?._id && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <ReferralsManager
            project={project}
            forms={forms}
            userRole={userRole}
            formId={formData._id}
            lockedFormId
            compact
          />
        </div>
      )}
      </>
      )}
    </div>
  );
}

function FieldEditor({ field, index, total, expanded, onToggleExpand, onUpdate, onRemove, onMoveUp, onMoveDown, disabled }) {
  const nameHint = field.name === 'referral_code'
    ? 'Used for ?ref= auto-fill and referral tracking'
    : null;

  return (
    <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {!disabled && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }} onClick={e => e.stopPropagation()}>
            <button
              className="btn-ghost"
              title="Move up"
              disabled={index === 0}
              onClick={onMoveUp}
              style={{ fontSize: 10, padding: '2px 6px', opacity: index === 0 ? 0.35 : 1, minWidth: 28 }}
            >↑</button>
            <button
              className="btn-ghost"
              title="Move down"
              disabled={index >= total - 1}
              onClick={onMoveDown}
              style={{ fontSize: 10, padding: '2px 6px', opacity: index >= total - 1 ? 0.35 : 1, minWidth: 28 }}
            >↓</button>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', flex: 1 }} onClick={onToggleExpand}>
          <span style={{ fontWeight: 500, fontSize: 13 }}>{field.label}</span>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>({field.type})</span>
          {field.required && <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>*required</span>}
          <span style={{ marginLeft: 'auto', color: 'var(--muted)' }}>
            {expanded ? '▼' : '▶'}
          </span>
        </div>
      </div>

      {expanded && !disabled && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Label</label>
            <input
              type="text"
              value={field.label}
              onChange={e => onUpdate({ label: e.target.value })}
              className="minput"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Field name (API key)</label>
            <input
              type="text"
              value={field.name}
              onChange={e => onUpdate({ name: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') })}
              className="minput"
            />
            {nameHint && <small style={{ color: 'var(--muted)' }}>{nameHint}</small>}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Field type</label>
            <select
              value={field.type}
              onChange={e => {
                const type = e.target.value;
                const needsOptions = ['checkbox', 'radio', 'select'].includes(type);
                onUpdate({
                  type,
                  options: needsOptions
                    ? (field.options && field.options.length ? field.options : ['Option 1', 'Option 2'])
                    : (field.options || [])
                });
              }}
              className="minput"
            >
              {FIELD_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Placeholder</label>
            <input
              type="text"
              value={field.placeholder || ''}
              onChange={e => onUpdate({ placeholder: e.target.value })}
              className="minput"
            />
          </div>

          {['checkbox', 'radio', 'select'].includes(field.type) && (
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Options</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(field.options || []).map((option, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="text"
                      value={option}
                      onChange={e => {
                        const newOptions = [...(field.options || [])];
                        newOptions[idx] = e.target.value;
                        onUpdate({ options: newOptions });
                      }}
                      className="minput"
                      placeholder="Option name"
                      style={{ flex: 1 }}
                    />
                    <button
                      className="btn-ghost"
                      style={{ fontSize: 11, padding: '4px 8px', minWidth: '40px' }}
                      onClick={() => {
                        const newOptions = (field.options || []).filter((_, i) => i !== idx);
                        onUpdate({ options: newOptions });
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  className="btn-ghost"
                  style={{ fontSize: 11, padding: '4px 8px', width: 'fit-content' }}
                  onClick={() => {
                    const newOptions = [...(field.options || []), `Option ${(field.options || []).length + 1}`];
                    onUpdate({ options: newOptions });
                  }}
                >
                  + Add Option
                </button>
              </div>
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={field.required || false}
              onChange={e => onUpdate({ required: e.target.checked })}
            />
            <span style={{ fontSize: 12 }}>Required field</span>
          </label>

          <button className="btn-ghost" style={{ fontSize: 12, padding: '5px 10px', alignSelf: 'flex-start' }} onClick={onRemove}>
            Remove Field
          </button>
        </div>
      )}
    </div>
  );
}
